import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  CubeIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EllipsisVerticalIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  CalendarDaysIcon,
  ChatBubbleLeftIcon,
  PaperClipIcon,
  PlusIcon,
  XMarkIcon,
  EyeIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { Modal, Button, Input } from '../../components/common';

// Import RTK Query hooks and Redux actions
import {
  useGetTasksQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation
} from '../../services/taskApi';
import { selectUser } from '../../store/slices/authSlice';

// Import components
import TaskForm from './components/TaskForm';
import TaskDetail from './components/TaskDetail';
import TaskFilters from './components/TaskFilters';
import TaskStats from './components/TaskStats';

const TaskManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useSelector(selectUser);

  // Local state for filters and UI
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  // Build query parameters based on active tab and filters
  const queryParams = useMemo(() => {
    const params = {
      page: 1,
      limit: 20
    };

    // Add tab-specific filters
    switch (activeTab) {
      case 0: // All tasks
        break;
      case 1: // My tasks
        params.assignedTo = 'me';
        break;
      case 2: // Assigned by me
        params.assignedBy = 'me';
        break;
      case 3: // Overdue
        params.dueDate = 'overdue';
        break;
      case 4: // Completed
        params.status = 'completed';
        break;
      default:
        break;
    }

    // Add search term if present
    if (searchTerm.trim()) {
      params.search = searchTerm.trim();
    }

    return params;
  }, [activeTab, searchTerm]);

  // RTK Query hooks
  const {
    data: tasksData,
    isLoading: loading,
    error,
    refetch
  } = useGetTasksQuery(queryParams, {
    pollingInterval: 0, // Disable polling, rely on socket invalidation
  });

  const [createTask] = useCreateTaskMutation();
  const [updateTask] = useUpdateTaskMutation();
  const [deleteTask] = useDeleteTaskMutation();

  // Extract data from RTK Query response
  const tasks = tasksData?.data?.tasks || [];
  const stats = tasksData?.data?.stats || {
    totalTasks: 0,
    pendingTasks: 0,
    inProgressTasks: 0,
    completedTasks: 0,
    overdueTasks: 0
  };
  const pagination = tasksData?.data?.pagination || {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  };

  // Local state for UI
  const [taskDialog, setTaskDialog] = useState({
    open: false,
    mode: 'create', // 'create' or 'edit'
    task: null
  });
  const [detailDialog, setDetailDialog] = useState({
    open: false,
    taskId: null
  });
  const [showMenu, setShowMenu] = useState(null);
  const [menuTask, setMenuTask] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    task: null
  });

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleCreateTask = () => {
    setTaskDialog({
      open: true,
      mode: 'create',
      task: null
    });
  };

  const handleEditTask = (task) => {
    setTaskDialog({
      open: true,
      mode: 'edit',
      task
    });
    setShowMenu(null);
  };

  const handleViewTask = (task) => {
    setDetailDialog({
      open: true,
      taskId: task._id
    });
  };

  const handleDeleteTask = (task) => {
    setDeleteDialog({
      open: true,
      task
    });
    setShowMenu(null);
  };

  const handleConfirmDelete = async () => {
    const { task } = deleteDialog;
    try {
      await deleteTask(task._id).unwrap();
      setSnackbar({
        open: true,
        message: 'Task deleted successfully',
        severity: 'success'
      });
      setDeleteDialog({ open: false, task: null });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to delete task',
        severity: 'error'
      });
    }
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialog({ open: false, task: null });
  };

  const handleUpdateTaskStatus = async (task, newStatus) => {
    try {
      const updates = { status: newStatus };

      // Auto-set progress based on status changes
      switch (newStatus) {
        case 'pending':
          updates.progress = 0;
          break;
        case 'in_progress':
          // If reopening from completed/cancelled, set to 25%, otherwise keep current progress
          if (task.status === 'completed' || task.status === 'cancelled') {
            updates.progress = 25;
          }
          break;
        case 'on_hold':
          // Keep current progress when putting on hold
          break;
        case 'completed':
          updates.progress = 100;
          break;
        case 'cancelled':
          updates.progress = 0;
          break;
        default:
          break;
      }

      await updateTask({ taskId: task._id, updates }).unwrap();
      setSnackbar({
        open: true,
        message: `Task marked as ${newStatus}`,
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update task',
        severity: 'error'
      });
    }
  };

  const handleMenuOpen = (taskId) => {
    setShowMenu(showMenu === taskId ? null : taskId);
  };

  const handleMenuClose = () => {
    setShowMenu(null);
  };

  const handleTaskDialogClose = () => {
    setTaskDialog({
      open: false,
      mode: 'create',
      task: null
    });
  };

  const handleDetailDialogClose = () => {
    setDetailDialog({
      open: false,
      task: null
    });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return '#d32f2f';
      case 'high': return '#f57c00';
      case 'medium': return '#1976d2';
      case 'low': return '#388e3c';
      default: return '#757575';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#4caf50';
      case 'in_progress': return '#2196f3';
      case 'on_hold': return '#ff9800';
      case 'pending': return '#9e9e9e';
      case 'cancelled': return '#f44336';
      default: return '#757575';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };



  // Tab data
  const tabs = [
    { id: 0, label: 'All Tasks', count: stats.totalTasks },
    { id: 1, label: 'My Tasks', count: null },
    { id: 2, label: 'Assigned by Me', count: null },
    { id: 3, label: 'Overdue', count: stats.overdueTasks },
    { id: 4, label: 'Completed', count: stats.completedTasks }
  ];

  return (
    <>
      <Helmet>
        <title>Task Management - IAR HRMS</title>
        <meta name="description" content="Manage tasks and assignments efficiently" />
      </Helmet>

      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-white/20 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center px-2 sm:px-4 py-2 text-gray-600 hover:text-gray-900 bg-white/50 rounded-lg sm:rounded-xl hover:bg-white/80 transition-all duration-200 group flex-shrink-0"
              >
                <ArrowLeftIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2 group-hover:-translate-x-1 transition-transform duration-200" />
                <span className="hidden xs:inline">Back</span>
              </button>
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent truncate flex items-center">
                  <CubeIcon className="h-6 w-6 sm:h-8 sm:w-8 mr-2 sm:mr-3 text-blue-600" />
                  Task Management
                </h1>
                <p className="text-xs sm:text-sm text-gray-600 mt-1 hidden sm:block">
                  Manage tasks, assignments, and track progress across your organization
                </p>
              </div>
            </div>
            <Button
              onClick={handleCreateTask}
              className="px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center space-x-2"
            >
              <PlusIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Create Task</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4">

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-white/60 p-4 shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircleIcon className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Completed</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.completedTasks || 0}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-white/60 p-4 shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <ClockIcon className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-700">In Progress</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.inProgressTasks || 0}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-white/60 p-4 shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Pending</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.pendingTasks || 0}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-white/60 p-4 shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Overdue</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.overdueTasks || 0}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filters and Search */}
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-white/60 p-6 mb-6 shadow-lg">
            <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
              <div className="flex-1 w-full lg:w-auto">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search tasks by title, description..."
                    className="pl-10 w-full py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300"
                  />
                </div>
              </div>

              <div className="flex gap-3 w-full lg:w-auto">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center px-4 py-3 border-2 border-gray-200 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-all duration-200 text-gray-700"
                >
                  <FunnelIcon className="h-4 w-4 mr-2" />
                  Filters
                </button>

                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="px-4 py-3 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-xl transition-all duration-200"
                  >
                    Clear Search
                  </button>
                )}
              </div>
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-gray-600">Advanced filters coming soon...</p>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-white/60 p-2 mb-6 shadow-lg">
            <div className="flex flex-wrap gap-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-4 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                      : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                  }`}
                >
                  {tab.label}
                  {tab.count !== null && tab.count > 0 && (
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-bold ${
                      activeTab === tab.id
                        ? 'bg-white/20 text-white'
                        : 'bg-blue-100 text-blue-600'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Tasks List */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
                <p className="text-gray-600 text-lg">Loading tasks...</p>
              </div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-6">
              <div className="flex items-start space-x-4">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-red-800 mb-2">Failed to load tasks</h3>
                  <p className="text-red-700 mb-4">{error?.data?.message || error?.message || 'An error occurred while loading tasks'}</p>
                  <button
                    onClick={() => refetch()}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          ) : tasks.length === 0 ? (
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl border-2 border-dashed border-blue-300 p-12 text-center shadow-lg">
              <div className="w-24 h-24 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CubeIcon className="h-12 w-12 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">No tasks found</h3>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">
                {activeTab === 0
                  ? "Get started by creating your first task. Organize your work and boost productivity!"
                  : "No tasks match the current filters. Try adjusting your search criteria."
                }
              </p>
              <Button
                onClick={handleCreateTask}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center space-x-3 mx-auto"
              >
                <PlusIcon className="h-5 w-5" />
                <span>Create Your First Task</span>
              </Button>
            </div>
          ) : (
            <div>
              {/* Task Count */}
              <div className="flex justify-between items-center mb-6">
                <p className="text-gray-600">
                  Showing <span className="font-semibold text-gray-900">{tasks.length}</span> task{tasks.length !== 1 ? 's' : ''}
                </p>
              </div>

              {/* Tasks Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {tasks.map((task, index) => (
                  <div
                    key={task._id}
                    className={`bg-white/90 backdrop-blur-sm rounded-2xl border shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2 relative ${
                      task.isOverdue
                        ? 'border-red-300 bg-gradient-to-br from-red-50 to-pink-50'
                        : task.status === 'completed'
                        ? 'border-green-300 bg-gradient-to-br from-green-50 to-emerald-50'
                        : 'border-blue-200 bg-gradient-to-br from-white to-blue-50'
                    }`}
                    style={{ animationDelay: `${index * 100}ms` }}
                    onClick={() => handleViewTask(task)}
                  >
                    {/* Status Bar */}
                    <div className={`h-1 w-full rounded-t-2xl ${
                      task.isOverdue
                        ? 'bg-gradient-to-r from-red-500 to-pink-500'
                        : task.status === 'completed'
                        ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                        : 'bg-gradient-to-r from-blue-500 to-indigo-500'
                    }`} />

                    <div className="p-6">
                      {/* Header */}
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1 min-w-0">
                          <h3 className={`text-lg font-bold text-gray-900 mb-2 truncate ${
                            task.isOverdue ? 'text-red-600' : ''
                          }`}>
                            {task.title}
                          </h3>

                          {/* Priority and Status Badges */}
                          <div className="flex flex-wrap gap-2 mb-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              task.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                              task.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                              task.priority === 'medium' ? 'bg-blue-100 text-blue-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              task.status === 'completed' ? 'bg-green-100 text-green-700' :
                              task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                              task.status === 'on_hold' ? 'bg-yellow-100 text-yellow-700' :
                              task.status === 'pending' ? 'bg-gray-100 text-gray-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {task.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </span>
                          </div>
                        </div>

                        {/* Menu Button */}
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMenuOpen(task._id);
                            }}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                          >
                            <EllipsisVerticalIcon className="h-5 w-5" />
                          </button>

                          {/* Dropdown Menu */}
                          {showMenu === task._id && (
                            <div className="absolute right-0 top-10 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50 min-w-48 pointer-events-auto">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewTask(task);
                                  handleMenuClose();
                                }}
                                className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                              >
                                <EyeIcon className="h-4 w-4 mr-3" />
                                View Details
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditTask(task);
                                  handleMenuClose();
                                }}
                                className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                              >
                                <PencilIcon className="h-4 w-4 mr-3" />
                                Edit Task
                              </button>
                              <div className="border-t border-gray-200 my-1"></div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteTask(task);
                                  handleMenuClose();
                                }}
                                className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200"
                              >
                                <TrashIcon className="h-4 w-4 mr-3" />
                                Delete Task
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Description */}
                      {task.description && (
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                          {task.description}
                        </p>
                      )}

                      {/* Progress Bar */}
                      <div className="mb-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-semibold text-gray-700">Progress</span>
                          <span className={`text-sm font-bold ${
                            task.progress === 100 ? 'text-green-600' : 'text-blue-600'
                          }`}>
                            {task.progress}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div
                            className={`h-2.5 rounded-full transition-all duration-300 ${
                              task.progress === 100
                                ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                                : 'bg-gradient-to-r from-blue-500 to-indigo-500'
                            }`}
                            style={{ width: `${task.progress}%` }}
                          ></div>
                        </div>
                        {task.progress === 100 && (
                          <div className="flex justify-end mt-1">
                            <CheckCircleIcon className="h-5 w-5 text-green-500" />
                          </div>
                        )}
                      </div>

                      {/* Assigned Users */}
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <UserGroupIcon className="h-4 w-4 text-gray-500" />
                            <span className="text-sm font-semibold text-gray-700">
                              Team ({task.assignedToUsers?.length || 0})
                            </span>
                          </div>
                        </div>
                        <div className="flex -space-x-2">
                          {task.assignedToUsers?.slice(0, 4).map((user, idx) => (
                            <div
                              key={user._id || idx}
                              className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 border-2 border-white flex items-center justify-center text-white text-xs font-bold shadow-sm"
                              title={`${user.firstName} ${user.lastName}`}
                            >
                              {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                            </div>
                          ))}
                          {task.assignedToUsers?.length > 4 && (
                            <div className="w-8 h-8 rounded-full bg-gray-400 border-2 border-white flex items-center justify-center text-white text-xs font-bold shadow-sm">
                              +{task.assignedToUsers.length - 4}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Comments Section */}
                      {task.comments && task.comments.length > 0 && (
                        <div className="mb-4">
                          <div className="flex items-center space-x-2 mb-2">
                            <ChatBubbleLeftIcon className="h-4 w-4 text-gray-500" />
                            <span className="text-sm font-semibold text-gray-700">
                              Recent Comments ({task.comments.length})
                            </span>
                          </div>
                          <div className="space-y-2 max-h-24 overflow-y-auto">
                            {task.comments.slice(0, 2).map((comment, idx) => (
                              <div key={idx} className="bg-gray-50 rounded-lg p-3">
                                <div className="flex items-start space-x-2">
                                  <div className="w-6 h-6 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                    {comment.userId?.firstName?.charAt(0)}{comment.userId?.lastName?.charAt(0)}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center space-x-2 mb-1">
                                      <span className="text-xs font-semibold text-gray-900">
                                        {comment.userId?.firstName} {comment.userId?.lastName}
                                      </span>
                                      <span className="text-xs text-gray-500">
                                        {formatDate(comment.createdAt)}
                                      </span>
                                    </div>
                                    <p className="text-xs text-gray-700 line-clamp-2">
                                      {comment.message}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                            {task.comments.length > 2 && (
                              <div className="text-xs text-gray-500 text-center py-1">
                                +{task.comments.length - 2} more comments
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Footer */}
                      <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                        <div className="flex items-center space-x-2">
                          <CalendarDaysIcon className={`h-4 w-4 ${
                            task.isOverdue ? 'text-red-500' : 'text-gray-500'
                          }`} />
                          <span className={`text-sm font-medium ${
                            task.isOverdue ? 'text-red-600' : 'text-gray-600'
                          }`}>
                            Due: {formatDate(task.dueDate)}
                          </span>
                        </div>

                        <div className="flex items-center space-x-2">
                          {task.attachments && task.attachments.length > 0 && (
                            <div className="flex items-center space-x-1 text-gray-500">
                              <PaperClipIcon className="h-4 w-4" />
                              <span className="text-xs font-medium">{task.attachments.length}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Overdue Alert */}
                      {task.isOverdue && (
                        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
                          <div className="flex items-center space-x-2">
                            <ExclamationTriangleIcon className="h-4 w-4 text-red-500 flex-shrink-0" />
                            <span className="text-sm font-semibold text-red-700">
                              Overdue by {Math.abs(task.daysRemaining)} day{Math.abs(task.daysRemaining) !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex justify-center mt-8">
              {/* Pagination component can be added here */}
            </div>
          )}
        </div>
      </div>

      {/* Task Form Dialog */}
      <TaskForm
        open={taskDialog.open}
        mode={taskDialog.mode}
        task={taskDialog.task}
        onClose={handleTaskDialogClose}
        onSuccess={() => {
          handleTaskDialogClose();
          setSnackbar({
            open: true,
            message: `Task ${taskDialog.mode === 'create' ? 'created' : 'updated'} successfully`,
            severity: 'success'
          });
        }}
      />

      {/* Task Detail Dialog */}
      <TaskDetail
        open={detailDialog.open}
        taskId={detailDialog.taskId}
        onClose={handleDetailDialogClose}
        onEdit={handleEditTask}
        onDelete={handleDeleteTask}
        onStatusUpdate={handleUpdateTaskStatus}
      />

      {/* Floating Action Button */}
      <button
        onClick={handleCreateTask}
        className="fixed bottom-6 right-6 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-200 z-50"
      >
        <PlusIcon className="h-6 w-6" />
      </button>

      {/* Snackbar */}
      {snackbar.open && (
        <div className={`fixed bottom-6 left-6 z-50 max-w-sm ${
          snackbar.severity === 'success' ? 'bg-green-500' :
          snackbar.severity === 'error' ? 'bg-red-500' : 'bg-blue-500'
        } text-white px-6 py-4 rounded-xl shadow-lg`}>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">{snackbar.message}</span>
            <button
              onClick={handleSnackbarClose}
              className="ml-4 text-white hover:text-gray-200 transition-colors duration-200"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Click outside to close menu */}
      {showMenu && (
        <div
          className="fixed inset-0 z-10 pointer-events-none"
          onClick={handleMenuClose}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {deleteDialog.open && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-900 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Delete Task
                    </h3>
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete this task?
                    </p>
                  </div>
                </div>

                {deleteDialog.task && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-700 italic">
                      "{deleteDialog.task.title}"
                    </p>
                  </div>
                )}

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={handleDeleteDialogClose}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200"
                  >
                    Delete Task
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TaskManagement;
